import { useCallback, useEffect, useState } from 'react';
import type { Recurso, Zona } from '../../api/ops/pagos';
import type { Union } from '../../api/ops/sala';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Segmentos } from '../../componentes/Segmentos';
import { Tabla } from '../../componentes/Tabla';
import { rolesDe } from '../../logica/acceso';
import { dinero } from '../../logica/dinero';
import { estadoDeError } from '../../logica/estados';
import { conVocabulario, useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import { rutaConApartado, type PropsPantalla } from '../tipos';
import { useContextoReservas } from '../reservas/contexto';
import { mayuscula } from '../reservas/textos';
import { Cierres } from './Cierres';
import { PanelRecurso, PanelUnion, PanelZona } from './Paneles';
import { ReservadosEventos } from './ReservadosEventos';
import { ASIGNACION, AYUDA, PERMISOS_RECURSOS as P, PRECIO, TIPO_RECURSO, lineaDeZona } from './textos';
import '../../componentes/apartado.css';
import '../reservas/reservas.css';
import '../sala/sala.css';
import './recursos.css';

/**
 * «Recursos» (Configuración, entrada `recursos`, 8.5): apartados Zonas y recursos · Cierres (8.8) · Reservados para eventos
 * (8.10) en `?apartado=`. En «Zonas y recursos», la lista de Zonas, Recursos o Uniones (con la flecha, como el diseño) y el
 * panel de cada uno. Propietario y Administrador crean y cambian (`reservas.configuracion.gestionar`); Encargado y
 * Recepción solo ven. Los recursos existen sin el bloque Plano y sala (G403).
 */
type Lista = 'zonas' | 'recursos' | 'uniones';
const LISTAS: readonly { valor: Lista; texto: string }[] = [
    { valor: 'zonas', texto: 'Zonas' },
    { valor: 'recursos', texto: 'Recursos' },
    { valor: 'uniones', texto: 'Uniones' },
];

interface Datos {
    zonas: Zona[];
    recursos: Recurso[];
    uniones: Union[];
}

async function todas<T>(pedir: (cursor: string | null) => Promise<{ data: T[]; next_cursor: string | null }>): Promise<T[]> {
    const lista: T[] = [];
    let cursor: string | null = null;
    for (let i = 0; i < 20; i += 1) {
        const p = await pedir(cursor);
        lista.push(...p.data);
        cursor = p.next_cursor;
        if (cursor === null) {
            break;
        }
    }
    return lista;
}

export function Recursos(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, membresia, nombreNegocio, navegar, busqueda, ruta } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const contexto = useContextoReservas(ops, negocio, membresia, nombreNegocio);
    const [datos, setDatos] = useState<Datos | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [lista, setLista] = useState<Lista>('zonas');
    const [abierto, setAbierto] = useState<{ tipo: 'zona' | 'recurso' | 'union'; id: string | null } | null>(null);
    const [nuevo, setNuevo] = useState(0);
    const ver = permisos.puede(P.ver);
    const gestiona = permisos.puede(P.gestionar) && !soloLectura;
    const recargar = useCallback(() => { setIntento((i) => i + 1); }, []);
    const base = ruta.split('?')[0] ?? ruta;
    const apartado = busqueda.get('apartado') ?? 'zonas';

    useEffect(() => {
        if (!ver) {
            return;
        }
        let vivo = true;
        Promise.all([
            todas((cursor) => ops.listZones(negocio, { cursor, limit: 100 })),
            todas((cursor) => ops.listResources(negocio, { cursor, limit: 100 })),
            todas((cursor) => ops.listResourceUnions(negocio, { cursor, limit: 100 })),
        ])
            .then(([zonas, recursos, uniones]) => {
                if (vivo) {
                    setDatos({ zonas: zonas.filter((z) => z.status === 'active'), recursos: recursos.filter((r) => r.status === 'active'), uniones: uniones.filter((u) => u.status === 'active') });
                    setError(null);
                }
            })
            .catch((e: unknown) => { if (vivo) { setError(e); } });
        return () => { vivo = false; };
    }, [ops, negocio, ver, intento]);

    const textoNuevo = lista === 'zonas' ? 'Nueva zona' : lista === 'recursos' ? `Nuevo ${contexto.palabra}` : 'Nueva unión';
    const cierra = permisos.puede(P.cierres) && !soloLectura;
    const acciones = apartado === 'zonas'
        ? (gestiona ? [{ clave: 'nuevo', texto: textoNuevo, icono: 'plus' as const, onClick: () => { setAbierto({ tipo: lista === 'zonas' ? 'zona' : lista === 'recursos' ? 'recurso' : 'union', id: null }); } }] : [])
        : apartado === 'cierres'
          ? (cierra ? [{ clave: 'cerrar', texto: 'Cerrar recursos', icono: 'calendar-x' as const, onClick: () => { setNuevo((n) => n + 1); } }] : [])
          : (gestiona ? [{ clave: 'vender', texto: `Poner ${contexto.palabra}s a la venta`, icono: 'ticket' as const, onClick: () => { setNuevo((n) => n + 1); } }] : []);
    useCabecera({
        ayuda: AYUDA,
        subtitulo: conVocabulario('Zonas y {recursos} con su capacidad, cierres y {recursos} para eventos.', contexto.palabra),
        acciones,
    });

    if (!ver) {
        return <EstadoSinPermiso seccion="Recursos" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }

    const apartados = [
        { valor: 'zonas', etiqueta: 'Zonas y recursos', icono: 'layers' as const },
        { valor: 'cierres', etiqueta: 'Cierres', icono: 'lock' as const },
        { valor: 'reservados', etiqueta: 'Reservados para eventos', icono: 'calendar-days' as const },
    ];
    const barra = (
        <div className="rv-en-barra">
            <Segmentos etiqueta="Apartados de recursos" movil={movil} items={apartados.map((a) => ({ etiqueta: a.etiqueta, icono: a.icono }))} valor={Math.max(0, apartados.findIndex((a) => a.valor === apartado))} onCambio={(i) => { navegar(rutaConApartado(base, apartados[i]?.valor ?? 'zonas')); }} />
        </div>
    );
    if (apartado === 'cierres') {
        return (
            <div className="rv-rs-pantalla">
                {barra}
                <Cierres
                    ops={ops}
                    negocio={negocio}
                    movil={movil}
                    soloLectura={soloLectura}
                    contexto={contexto}
                    nuevo={nuevo}
                    abrirCierre={busqueda.get('cierre')}
                    navegar={navegar}
                    onAbrir={(id) => { navegar(id === null ? rutaConApartado(base, 'cierres') : `${rutaConApartado(base, 'cierres')}&cierre=${encodeURIComponent(id)}`); }}
                />
            </div>
        );
    }
    if (apartado === 'reservados') {
        return (
            <div className="rv-rs-pantalla">
                {barra}
                <ReservadosEventos ops={ops} negocio={negocio} movil={movil} soloLectura={soloLectura} gestiona={gestiona} contexto={contexto} nuevo={nuevo} navegar={navegar} filtro={busqueda.get('filtro')} />
            </div>
        );
    }
    if (error !== null && datos === null) {
        const e = estadoDeError(error);
        return e.tipo === 'sin-permiso'
            ? <EstadoSinPermiso seccion="Recursos" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />
            : <EstadoError seccion="Recursos" codigoSoporte={e.codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    if (datos === null) {
        return <EstadoCargando seccion="Recursos" />;
    }

    const nombreZona = (id: string) => datos.zonas.find((z) => z.id === id)?.name ?? '';
    const palabraDe = (z: Zona | undefined) => (z?.unit ?? '').trim() || contexto.palabra;
    const idx = LISTAS.findIndex((l) => l.valor === lista);
    const cifra = lista === 'zonas' ? datos.zonas.length : lista === 'recursos' ? datos.recursos.length : datos.uniones.length;
    const cerrar = (cambio?: string) => {
        setAbierto(null);
        if (cambio !== undefined) {
            avisar({ tipo: 'ok', texto: cambio });
            recargar();
        }
    };

    return (
        <div className="rv-rs-pantalla">
            {barra}
            {soloLectura && <p className="rv-sl-nota">El negocio está en solo lectura: puedes ver las zonas y los recursos, pero no cambiarlos.</p>}
            {!gestiona && !soloLectura && <p className="rv-sl-nota">Puedes ver las zonas y los recursos. Los cambian el propietario y los administradores.</p>}
            <div className="rv-sl-filtro">
                <button type="button" className="rv-sl-filtro__pastilla" aria-label={`Lista: ${LISTAS[idx]?.texto ?? ''}, ${String(cifra)}`}>
                    <span>{LISTAS[idx]?.texto}</span>
                    <b>{cifra}</b>
                </button>
                <button type="button" className="rv-sl-redondo rv-sl-redondo--chico" aria-label="Ver la lista siguiente" onClick={() => { setLista(LISTAS[(idx + 1) % LISTAS.length]?.valor ?? 'zonas'); }}>
                    <Icono nombre="chevron-right" />
                </button>
            </div>
            {lista === 'zonas' && (
                <Tabla
                    etiqueta="Zonas"
                    movil={movil}
                    columnas={[
                        { clave: 'zona', titulo: 'Zona', ancho: 'minmax(0, 2.4fr)' },
                        { clave: 'aforo', titulo: 'Aforo' },
                        { clave: 'precio', titulo: 'Precio', ancho: 'minmax(0, 1.4fr)' },
                        { clave: 'cobro', titulo: 'Cobro', ancho: 'minmax(0, 1.4fr)' },
                        { clave: 'asignacion', titulo: 'Asignación' },
                        { clave: 'recursos', titulo: 'Recursos', alinear: 'derecha' },
                    ]}
                    vacia={<p className="rv-sl-nota rv-sl-nota--centro">Aún no hay zonas. Crea la primera con «Nueva zona».</p>}
                    filas={datos.zonas.map((z) => {
                        const suyos = datos.recursos.filter((r) => r.current_terms.zone_id === z.id);
                        const aforo = z.seat_capacity ?? suyos.reduce((n, r) => n + r.current_terms.max_capacity, 0);
                        return {
                            clave: z.id,
                            etiqueta: z.name,
                            onAbrir: () => { setAbierto({ tipo: 'zona', id: z.id }); },
                            tarjeta: <span><strong>{z.name}</strong><br /><small>{lineaDeZona(z)}</small></span>,
                            celdas: {
                                zona: <span><strong>{z.name}</strong><br /><small>{lineaDeZona(z)}</small></span>,
                                aforo: <b>{`${String(aforo)} pers.`}</b>,
                                precio: z.price_mode === undefined || z.price_mode === 'free' || z.price === undefined ? 'Sin precio' : `${dinero(z.price)} ${PRECIO[z.price_mode] ?? ''}`,
                                cobro: z.policy_name ?? 'Sin cobro',
                                asignacion: ASIGNACION[z.assignment_mode ?? ''] ?? '—',
                                recursos: <b>{z.resource_count ?? suyos.length}</b>,
                            },
                        };
                    })}
                />
            )}
            {lista === 'recursos' && (
                <Tabla
                    etiqueta="Recursos"
                    movil={movil}
                    columnas={[
                        { clave: 'codigo', titulo: mayuscula(contexto.palabra) },
                        { clave: 'tipo', titulo: 'Tipo' },
                        { clave: 'zona', titulo: 'Zona' },
                        { clave: 'capacidad', titulo: 'Capacidad' },
                        { clave: 'junta', titulo: 'Se junta' },
                    ]}
                    vacia={<p className="rv-sl-nota rv-sl-nota--centro">{`Aún no hay ningún ${contexto.palabra}.`}</p>}
                    filas={datos.recursos.map((r) => ({
                        clave: r.id,
                        etiqueta: `${mayuscula(palabraDe(datos.zonas.find((z) => z.id === r.current_terms.zone_id)))} ${r.code ?? ''}`,
                        onAbrir: () => { setAbierto({ tipo: 'recurso', id: r.id }); },
                        celdas: {
                            codigo: <strong>{r.code}</strong>,
                            tipo: TIPO_RECURSO[r.kind] ?? r.kind,
                            zona: nombreZona(r.current_terms.zone_id),
                            capacidad: `${String(r.current_terms.min_capacity)}–${String(r.current_terms.max_capacity)}`,
                            junta: r.current_terms.joinable ? 'Sí' : 'No',
                        },
                    }))}
                />
            )}
            {lista === 'uniones' && (
                <Tabla
                    etiqueta="Uniones"
                    movil={movil}
                    columnas={[
                        { clave: 'nombre', titulo: 'Unión', ancho: 'minmax(0, 1.4fr)' },
                        { clave: 'zona', titulo: 'Zona' },
                        { clave: 'recursos', titulo: 'Junta', ancho: 'minmax(0, 1.6fr)' },
                        { clave: 'capacidad', titulo: 'Capacidad' },
                    ]}
                    vacia={<p className="rv-sl-nota rv-sl-nota--centro">Sin uniones predefinidas. En Sala se pueden juntar en el momento.</p>}
                    filas={datos.uniones.map((u) => ({
                        clave: u.id,
                        etiqueta: u.name ?? 'Unión',
                        onAbrir: () => { setAbierto({ tipo: 'union', id: u.id }); },
                        celdas: {
                            nombre: <strong>{u.name ?? 'Unión'}</strong>,
                            zona: nombreZona(u.zone_id),
                            recursos: (u.resource_ids ?? []).map((id) => datos.recursos.find((r) => r.id === id)?.code ?? '').join(' + '),
                            capacidad: `${String(u.min_capacity ?? 1)}–${String(u.max_capacity ?? 1)}`,
                        },
                    }))}
                />
            )}

            {abierto?.tipo === 'zona' && (
                <PanelZona
                    ops={ops}
                    negocio={negocio}
                    movil={movil}
                    zona={datos.zonas.find((z) => z.id === abierto.id) ?? null}
                    recursos={datos.recursos}
                    palabra={contexto.palabra}
                    gestiona={gestiona}
                    puedeHorario={permisos.puede(P.horario)}
                    onHorario={() => { navegar('/horario'); }}
                    onCerrar={cerrar}
                />
            )}
            {abierto?.tipo === 'recurso' && (
                <PanelRecurso ops={ops} negocio={negocio} movil={movil} recurso={datos.recursos.find((r) => r.id === abierto.id) ?? null} zonas={datos.zonas} palabra={contexto.palabra} gestiona={gestiona} onCerrar={cerrar} />
            )}
            {abierto?.tipo === 'union' && (
                <PanelUnion ops={ops} negocio={negocio} movil={movil} union={datos.uniones.find((u) => u.id === abierto.id) ?? null} zonas={datos.zonas} recursos={datos.recursos} gestiona={gestiona} onCerrar={cerrar} />
            )}
        </div>
    );
}
